import { useEffect, useState } from 'react'
import { inspectWall, type ProjectedPerson, type WallDisplayPreferences } from '../features/sharing/api'
import { defaultDisplayPreferences, formatPoints, wallHeadlineOrder, type WallHeadline } from '../features/display/preferences'
import { useWallFragmentToken } from '../offline'
import { AccumulationDisplay } from '../components/AccumulationDisplay'
import { getAuthenticatedOwnerId } from '../offline/supabase'
import { settingsHref } from './settingsRouting'

export const WALL_REFRESH_INTERVAL_MS = 15_000

const fallbackWallDisplay: WallDisplayPreferences = {
  wallHeadlines: defaultDisplayPreferences.wallHeadlines,
  wallAccumulation: defaultDisplayPreferences.wallAccumulation,
  wallCategories: defaultDisplayPreferences.wallCategories,
  wallSuccessfulItems: defaultDisplayPreferences.wallSuccessfulItems,
}

function normalizedDisplay(preferences: WallDisplayPreferences): WallDisplayPreferences {
  const requested = preferences.wallHeadlines?.length ? preferences.wallHeadlines : preferences.wallHeadline ? [preferences.wallHeadline] : ['today']
  return {
    ...fallbackWallDisplay,
    ...preferences,
    wallHeadlines: wallHeadlineOrder.filter((headline) => requested.includes(headline)),
  }
}

function headlineValue(headline: WallHeadline, projection: ProjectedPerson['projection']): number | null {
  return headline === 'today' ? projection.score : headline === '7-day' ? projection.trails[0] : projection.trails[1]
}

const headlineLabel: Record<WallHeadline, string> = { today: 'Today', '7-day': '7-day', '28-day': '28-day' }

type WallState = {
  phase: 'loading' | 'ready' | 'error'
  refresh: 'idle' | 'delayed'
  people: ProjectedPerson[]
  generatedAt?: string
  display: WallDisplayPreferences
}

type WallPresentationProps = {
  people: ProjectedPerson[]
  generatedAt?: string
  preferences: WallDisplayPreferences
  refresh?: WallState['refresh']
  configureHref?: string
}

export function WallPresentation({ people, generatedAt, preferences, refresh = 'idle', configureHref }: WallPresentationProps) {
  const accumulationMax = Math.max(1, ...people.map((person) => person.projection.accumulation ?? 0))
  const generatedLabel = generatedAt ? new Date(generatedAt).toLocaleString() : 'Read-only wall'
  const display = normalizedDisplay(preferences)

  return <>
    <section className="wall-stage" aria-labelledby="wall-title">
      <h1 className="sr-only" id="wall-title">Ops Matrix wall</h1>
      {people.length === 0 ? <p className="wall-message" role="status">No shared people are selected for this wall.</p> : <div className="wall-board" data-count={Math.min(people.length, 4)}>
        {people.map(({ projection, stale }, index) => {
          const selected = display.wallHeadlines.length ? display.wallHeadlines : ['today' as const]
          const heroKey = selected[0]
          const headline = headlineValue(heroKey, projection)
          const heroLabel = headlineLabel[heroKey]
          const freshness = projection.freshness ? new Date(projection.freshness).toLocaleString() : 'No recent sync'
          const headingId = `wall-person-${index}`
          return <article className={`wall-tile ${stale ? 'stale' : ''}`} aria-labelledby={headingId} key={`${projection.displayName}-${index}`}>
            <h2 id={headingId}>{projection.displayName}</h2>
            {stale ? <p className="wall-stale">Score unavailable · {freshness}</p> : <>
              <span className="overline">{heroLabel}</span>
              <strong className={`wall-score wall-score-count-${selected.length} ${headline == null ? 'wall-score--empty' : ''}`} aria-label={headline == null ? `No ${heroLabel} score` : `${formatPoints(headline)}`}>{formatPoints(headline)}</strong>
              {headline == null ? <p className="wall-empty-score">No {heroLabel.toLowerCase()} score</p> : <progress className="earned-line" value={headline} max="100" aria-label={`${projection.displayName} ${heroLabel} earned score ${formatPoints(headline)}`} />}
              <div className="wall-secondary-headlines">{selected.slice(1).map((key) => <span key={key}><small>{headlineLabel[key]}</small><strong>{formatPoints(headlineValue(key, projection))}</strong></span>)}</div>
            </>}
            {display.wallCategories && projection.categories && projection.categories.length > 0 && <section className="wall-categories" aria-label={`${projection.displayName} earned category scores`}>{projection.categories.map((category) => <span key={category.name}>{category.name}<strong>{formatPoints(category.earnedScore)}</strong></span>)}</section>}
            {display.wallSuccessfulItems && projection.successfulItems && projection.successfulItems.length > 0 && <section className="earned-items" aria-label={`${projection.displayName} successful items`}>{projection.successfulItems.map((item) => <span key={item}>{item}</span>)}</section>}
            {!stale && <AccumulationDisplay mode={display.wallAccumulation} total={projection.accumulation} maximum={accumulationMax} name={projection.displayName} freshness={freshness} singlePerson={people.length === 1} surface="wall" />}
          </article>
        })}
      </div>}
    </section>
    <footer className="wall-footer">
      <time dateTime={generatedAt}>{generatedLabel}</time>
      <span aria-live="polite">Codex staging · read-only display{refresh === 'delayed' ? ' · update delayed · retrying' : ''}</span>
      {configureHref && <a className="button" href={configureHref}>Configure Wall</a>}
    </footer>
  </>
}

export function WallScreen() {
  const token = useWallFragmentToken()
  const [ownerCanConfigure, setOwnerCanConfigure] = useState(false)
  const [state, setState] = useState<WallState>({ phase: token ? 'loading' : 'error', refresh: 'idle', people: [], display: fallbackWallDisplay })
  useEffect(() => {
    if (!token) return
    let active = true
    let inFlight = false

    const refreshWall = async () => {
      if (!active || inFlight) return
      inFlight = true
      try {
        const value = await inspectWall(token)
        if (active) setState({ phase: 'ready', refresh: 'idle', people: value.people, generatedAt: value.generatedAt, display: normalizedDisplay(value.display ?? fallbackWallDisplay) })
      } catch {
        if (active) setState((current) => current.phase === 'ready'
          ? { ...current, refresh: 'delayed' }
          : { phase: 'error', refresh: 'idle', people: [], display: fallbackWallDisplay })
      } finally {
        inFlight = false
      }
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshWall()
    }
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refreshWall() }, WALL_REFRESH_INTERVAL_MS)
    window.addEventListener('focus', refreshWhenVisible)
    window.addEventListener('online', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    void refreshWall()

    return () => {
      active = false
      window.clearInterval(timer)
      window.removeEventListener('focus', refreshWhenVisible)
      window.removeEventListener('online', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [token])
  useEffect(() => {
    let active = true
    void getAuthenticatedOwnerId().then((ownerId) => { if (active) setOwnerCanConfigure(ownerId !== null) }).catch(() => undefined)
    return () => { active = false }
  }, [])

  const configureHref = ownerCanConfigure ? settingsHref('public-wall-display', window.location.search) : undefined

  return (
    <main className="wall-screen">
      {state.phase === 'loading' && <section className="wall-stage wall-stage--message"><p className="wall-message" role="status">Checking this read-only wall link…</p></section>}
      {state.phase === 'error' && <section className="wall-stage wall-stage--message"><p className="wall-message" role="alert">This wall link is invalid, expired, revoked, or sharing is not configured.</p></section>}
      {state.phase === 'ready' && <WallPresentation people={state.people} generatedAt={state.generatedAt} preferences={state.display} refresh={state.refresh} configureHref={configureHref} />}
      {state.phase !== 'ready' && <footer className="wall-footer"><span>Read-only wall</span><span>Codex staging · read-only display</span>{configureHref && <a className="button" href={configureHref}>Configure Wall</a>}</footer>}
    </main>
  )
}
